/**
 * The timing chart of one request (design "Traces: one request"): every step's bar on one clock, from 0 to the
 * request's duration, as percentages of the track, and the time axis above it. Pure, so it is tested without a browser.
 */

/** The clock's length: the request's duration, or the end of its last step when one runs past it (never 0). */
export function clockLength(rows = [], totalMs = 0) {
  const ends = rows.map((r) => (Number(r.offsetMs) || 0) + (Number(r.durationMs) || 0));
  const total = Math.max(Number(totalMs) || 0, ...ends, 0);
  return total > 0 ? total : 1;
}

/**
 * Where a step's bar goes, in % of the track: `{ left, width }`. A step too short to see still gets `minPct`, and a
 * bar never runs off the end.
 */
export function barLayout(row, total, { minPct = 0.6 } = {}) {
  const clock = total > 0 ? total : 1;
  const offset = Math.max(0, Number(row?.offsetMs) || 0);
  const duration = Math.max(0, Number(row?.durationMs) || 0);
  const width = Math.min(100, Math.max(minPct, (duration / clock) * 100));
  const left = Math.min(Math.max(0, (offset / clock) * 100), 100 - width);
  return { left: round(left), width: round(width) };
}

const round = (n) => Math.round(n * 100) / 100;

/** The round step nearest to `target` intervals over `total` ms: 1, 2 or 5 times a power of ten. */
export function niceStep(total, target = 4) {
  if (!(total > 0)) { return 1; }
  const raw = total / target;
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / power;
  const nice = unit <= 1.5 ? 1 : unit <= 3.5 ? 2 : unit <= 7.5 ? 5 : 10;
  return nice * power;
}

/** A duration for people: "0.4 ms", "184 ms", "1.2 s". */
export function formatMs(ms) {
  const value = Number(ms) || 0;
  if (value >= 10000) { return `${Math.round(value / 100) / 10} s`; }
  if (value >= 1000) { return `${Math.round(value / 10) / 100} s`; }
  if (value > 0 && value < 1) { return `${Math.round(value * 10) / 10} ms`; }
  return `${Math.round(value)} ms`;
}

/**
 * The axis: `[{ ms, pct, label }]` from 0 to the end, a tick every round step ("0", "50 ms", "100 ms", …) and the end
 * itself ("184"). A round tick too close to the end is left out so the labels do not touch.
 */
export function axisTicks(total, { target = 4 } = {}) {
  const clock = total > 0 ? total : 1;
  const step = niceStep(clock, target);
  const ticks = [{ ms: 0, pct: 0, label: '0' }];
  for (let ms = step; ms < clock - step * 0.4; ms += step) {
    ticks.push({ ms, pct: round((ms / clock) * 100), label: formatMs(ms) });
  }
  const end = Math.round(clock) || round(clock);
  ticks.push({ ms: clock, pct: 100, label: String(clock < 1 ? round(clock) : end) });
  return ticks;
}
